import { action, makeObservable, observable } from "mobx";
import type { InjectionToken } from "tsyringe";

export interface AppSettingsSnapshot {
  realtimeEnabled: boolean;
  showSystemEventsInDashboard: boolean;
  browserNotificationsEnabled: boolean;
  webPushEnabled: boolean;
  takenItemsRemindersEnabled: boolean;
  aiPhotoAutofillEnabled: boolean;
}

export interface ISettingsStore {
  realtimeEnabled: boolean;
  showSystemEventsInDashboard: boolean;
  browserNotificationsEnabled: boolean;
  webPushEnabled: boolean;
  takenItemsRemindersEnabled: boolean;
  aiPhotoAutofillEnabled: boolean;
  setRealtimeEnabled: (value: boolean) => void;
  setShowSystemEventsInDashboard: (value: boolean) => void;
  setBrowserNotificationsEnabled: (value: boolean) => void;
  setWebPushEnabled: (value: boolean) => void;
  setTakenItemsRemindersEnabled: (value: boolean) => void;
  setAiPhotoAutofillEnabled: (value: boolean) => void;
  reset: () => void;
}

export const SETTINGS_STORE_TOKEN = Symbol(
  "ISettingsStore",
) as InjectionToken<ISettingsStore>;

const SETTINGS_STORAGE_KEY = "korobochka.settings.v1";

export const DEFAULT_APP_SETTINGS: AppSettingsSnapshot = {
  realtimeEnabled: true,
  showSystemEventsInDashboard: false,
  browserNotificationsEnabled: false,
  webPushEnabled: false,
  takenItemsRemindersEnabled: true,
  aiPhotoAutofillEnabled: true,
};

function parseStoredSettings(raw: string | null): AppSettingsSnapshot | null {
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<AppSettingsSnapshot>;
    return {
      realtimeEnabled:
        typeof parsed.realtimeEnabled === "boolean"
          ? parsed.realtimeEnabled
          : DEFAULT_APP_SETTINGS.realtimeEnabled,
      showSystemEventsInDashboard:
        typeof parsed.showSystemEventsInDashboard === "boolean"
          ? parsed.showSystemEventsInDashboard
          : DEFAULT_APP_SETTINGS.showSystemEventsInDashboard,
      browserNotificationsEnabled:
        typeof parsed.browserNotificationsEnabled === "boolean"
          ? parsed.browserNotificationsEnabled
          : DEFAULT_APP_SETTINGS.browserNotificationsEnabled,
      webPushEnabled:
        typeof parsed.webPushEnabled === "boolean"
          ? parsed.webPushEnabled
          : DEFAULT_APP_SETTINGS.webPushEnabled,
      takenItemsRemindersEnabled:
        typeof parsed.takenItemsRemindersEnabled === "boolean"
          ? parsed.takenItemsRemindersEnabled
          : DEFAULT_APP_SETTINGS.takenItemsRemindersEnabled,
      aiPhotoAutofillEnabled:
        typeof parsed.aiPhotoAutofillEnabled === "boolean"
          ? parsed.aiPhotoAutofillEnabled
          : DEFAULT_APP_SETTINGS.aiPhotoAutofillEnabled,
    };
  } catch {
    return null;
  }
}

function canUseStorage(): boolean {
  return (
    typeof window !== "undefined" && typeof window.localStorage !== "undefined"
  );
}

export class SettingsStore implements ISettingsStore {
  @observable
  realtimeEnabled = DEFAULT_APP_SETTINGS.realtimeEnabled;

  @observable
  showSystemEventsInDashboard =
    DEFAULT_APP_SETTINGS.showSystemEventsInDashboard;

  @observable
  browserNotificationsEnabled =
    DEFAULT_APP_SETTINGS.browserNotificationsEnabled;

  @observable
  webPushEnabled = DEFAULT_APP_SETTINGS.webPushEnabled;

  @observable
  takenItemsRemindersEnabled = DEFAULT_APP_SETTINGS.takenItemsRemindersEnabled;

  @observable
  aiPhotoAutofillEnabled = DEFAULT_APP_SETTINGS.aiPhotoAutofillEnabled;

  constructor() {
    makeObservable(this);
    this.hydrate();
  }

  @action
  setRealtimeEnabled = (value: boolean): void => {
    this.realtimeEnabled = value;
    this.persist();
  };

  @action
  setShowSystemEventsInDashboard = (value: boolean): void => {
    this.showSystemEventsInDashboard = value;
    this.persist();
  };

  @action
  setBrowserNotificationsEnabled = (value: boolean): void => {
    this.browserNotificationsEnabled = value;
    this.persist();
  };

  @action
  setWebPushEnabled = (value: boolean): void => {
    this.webPushEnabled = value;
    this.persist();
  };

  @action
  setTakenItemsRemindersEnabled = (value: boolean): void => {
    this.takenItemsRemindersEnabled = value;
    this.persist();
  };

  @action
  setAiPhotoAutofillEnabled = (value: boolean): void => {
    this.aiPhotoAutofillEnabled = value;
    this.persist();
  };

  @action
  reset = (): void => {
    this.realtimeEnabled = DEFAULT_APP_SETTINGS.realtimeEnabled;
    this.showSystemEventsInDashboard =
      DEFAULT_APP_SETTINGS.showSystemEventsInDashboard;
    this.browserNotificationsEnabled =
      DEFAULT_APP_SETTINGS.browserNotificationsEnabled;
    this.webPushEnabled = DEFAULT_APP_SETTINGS.webPushEnabled;
    this.takenItemsRemindersEnabled =
      DEFAULT_APP_SETTINGS.takenItemsRemindersEnabled;
    this.aiPhotoAutofillEnabled = DEFAULT_APP_SETTINGS.aiPhotoAutofillEnabled;
    this.persist();
  };

  private hydrate(): void {
    if (!canUseStorage()) {
      return;
    }

    const parsed = parseStoredSettings(
      window.localStorage.getItem(SETTINGS_STORAGE_KEY),
    );

    if (!parsed) {
      return;
    }

    this.realtimeEnabled = parsed.realtimeEnabled;
    this.showSystemEventsInDashboard = parsed.showSystemEventsInDashboard;
    this.browserNotificationsEnabled = parsed.browserNotificationsEnabled;
    this.webPushEnabled = parsed.webPushEnabled;
    this.takenItemsRemindersEnabled = parsed.takenItemsRemindersEnabled;
    this.aiPhotoAutofillEnabled = parsed.aiPhotoAutofillEnabled;
  }

  private persist(): void {
    if (!canUseStorage()) {
      return;
    }

    const snapshot: AppSettingsSnapshot = {
      realtimeEnabled: this.realtimeEnabled,
      showSystemEventsInDashboard: this.showSystemEventsInDashboard,
      browserNotificationsEnabled: this.browserNotificationsEnabled,
      webPushEnabled: this.webPushEnabled,
      takenItemsRemindersEnabled: this.takenItemsRemindersEnabled,
      aiPhotoAutofillEnabled: this.aiPhotoAutofillEnabled,
    };

    try {
      window.localStorage.setItem(
        SETTINGS_STORAGE_KEY,
        JSON.stringify(snapshot),
      );
    } catch {
      // Игнорируем ошибки quota/storage access.
    }
  }
}
